import React, { useState } from 'react';
import { TextField, Button, Alert, Box, Typography, CircularProgress } from '@mui/material';
import { authService } from '../../services/authService';
import { Link } from 'react-router-dom';

const ForgotPasswordForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    if (!email) {
      setError('Please enter your email address');
      setLoading(false);
      return;
    }

    try {
      const response = await authService.requestPasswordReset(email);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send password reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', p: 2 }}>
      <Typography variant="h5" component="h1" gutterBottom>
        Reset Password
      </Typography>
      
      {success ? (
        <Box>
          <Alert severity="success" sx={{ mb: 2 }}>
            If your email exists in our system, you will receive a password reset link shortly.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please check your email and follow the instructions to reset your password.
          </Typography>
          <Button component={Link} to="/login" variant="outlined" fullWidth>
            Return to Login
          </Button>
        </Box>
      ) : (
        <form onSubmit={handleSubmit}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter your email address and we'll send you a link to reset your password.
          </Typography>
          
          <TextField
            name="email"
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            margin="normal"
            required
          />
          
          <Button 
            type="submit" 
            variant="contained" 
            color="primary" 
            fullWidth 
            disabled={loading}
            sx={{ mt: 2, mb: 2 }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
          </Button>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Button component={Link} to="/login" variant="text" size="small">
              Back to Login
            </Button>
          </Box>
        </form>
      )}
    </Box>
  );
};

export default ForgotPasswordForm;
